import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    InputSignal,
    output,
    OutputEmitterRef,
    Signal,
    signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {onEach} from '../../utils/observable-utils';
import {MenuItemSelection, MenuModel} from "../menu.model";
import {DietaryPanelComponent} from '../../order/order-item/dietary-panel/dietary-panel.component';
import {OrderModel} from '../../order/order.model';
import {ButtonComponent} from "../../buttons/button/button.component";
import {ButtonProminence, ButtonTier} from "../../buttons/button/button";
import {IconButtonType} from "../../buttons/icon-button/icon-button";
import {FedeltaImageData} from "@fedelta-pos/data-client";
import {PromoModel} from '../../promo/promo.model';
import {CurrencyPipe} from '../../utils/currency.pipe';
import {MenuCategoryData, MenuItemData} from "../menu-entity.model";
import {AnalyticsService} from '../../logging/analytics.service';
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {IconComponent} from "../../ui-elements/icon/icon.component";
import {ToggleComponent} from "../../ui-elements/toggle/toggle.component";
import {TouchableDirective} from "../../ui-elements/touchable.directive";
import {ImageCacheDirective} from '../../ui-elements/image/image-cache.directive';
import {HtmlPipe} from '../../utils/html.pipe';
import {LiteralPipe} from '../../utils/literal.pipe';
import {ConfigurationModel} from '../../configuration/configuration.model';
import {ActivatableDirective} from '../../ui-elements/activatable.directive';

@Component({
    selector: "woa-menu-item-panel",
    templateUrl: "./menu-item-panel.component.html",
    styleUrl: "./menu-item-panel.component.scss",
    host: {
        "[class.tile]": "tile()"
    },
    imports: [
        ButtonComponent,
        CurrencyPipe,
        DietaryPanelComponent,
        HtmlPipe,
        IconComponent,
        ImageCacheDirective,
        LiteralPipe,
        NgClass,
        ToggleComponent,
        TouchableDirective,
        TranslateModule,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MenuItemPanelComponent {
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public menuModel: MenuModel = inject(MenuModel);
    public promoModel: PromoModel = inject(PromoModel);
    private changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private analyticsService: AnalyticsService = inject(AnalyticsService);
    public orderModel: OrderModel = inject(OrderModel);

    ButtonTier = ButtonTier;
    ButtonProminence = ButtonProminence;
    IconButtonType = IconButtonType;

    public readonly category: InputSignal<MenuCategoryData> = input.required<MenuCategoryData>();
    public readonly menuItem: InputSignal<MenuItemData> = input.required<MenuItemData>();

    public readonly tile: InputSignal<boolean> = input<boolean>(false);
    public readonly showCategoryPath: InputSignal<boolean> = input<boolean>(false);
    public readonly showDetails: InputSignal<boolean> = input<boolean>(true);
    public readonly fromSearch: InputSignal<boolean> = input<boolean>(false);

    public onSelectItem: OutputEmitterRef<void> = output<void>();

    public readonly sizeSelectionER: Signal<ElementRef | undefined> = viewChild("sizeSelection");

    public readonly showSizeSelection: WritableSignal<boolean> = signal<boolean>(false);

    public readonly informational: Signal<boolean> = computed<boolean>(() =>
        this.category()?.informational || this.menuItem()?.informational);

    public readonly image: Signal<FedeltaImageData> = computed<FedeltaImageData>(() =>
        this.menuItem() ? this.menuModel.itemImage()(this.category(), this.menuItem(), undefined, true) : undefined);

    public readonly featureTag: Signal<string> = computed<string>(() =>
        this.menuItem() ? this.promoModel.promotedFeatureTag()(this.category(), this.menuItem()) : undefined);

    public readonly showOutOfStock: Signal<boolean> = computed<boolean>(() =>
        !this.informational() && this.menuModel.allSizesOutOfStock()(this.menuItem()));

    public readonly showAtLimit: Signal<boolean> = computed<boolean>(() =>
        !this.informational() && !this.menuModel.allSizesOutOfStock()(this.menuItem()) && !this.menuModel.itemOutOfStock()(this.menuItem()) && this.orderModel.allSizesAtLimit()(this.menuItem(), this.menuItem().sellable.minWebOrderQty));

    public readonly showUnavailable: Signal<boolean> = computed<boolean>(() =>
        this.showOutOfStock() || this.showAtLimit());

    public readonly canShowDetails: Signal<boolean> = computed<boolean>(() =>
        this.menuItem() && this.showDetails() && this.menuModel.getMenuItemState(this.menuItem()).isFullyInitialised());

    constructor() {
        // Lazy initialisation of menu items
        onEach(this.menuModel.menuItemInitialised$, async (menuItem: MenuItemData) => {
            if (menuItem === this.menuItem()) {
                this.changeDetectorRef.markForCheck();
                this.setSizeSelectionHeight();
            }
        });
    }

    public selectItem(orderSimpleProductsDirectly: boolean = false, ignoreSizes: boolean = false, requiredExtrasOnly: boolean = false, menuItem?: MenuItemData, directAdd?: ActivatableDirective): void {
        if (!menuItem) {
            menuItem = this.menuItem();
        } else if (menuItem === this.menuItem()) {
            void this.analyticsService.trackQuickAddSelected();
        } else {
            void this.analyticsService.trackQuickAddSizeSelected();
        }

        if (this.category().pairingsCategory) {
            void this.analyticsService.trackPairingsShown();
            void this.analyticsService.trackPairingSelected();
        } else if (this.fromSearch()) {
            void this.analyticsService.trackSearchItemSelected();
        }

        this.orderModel.requestOrderItem$.next(new MenuItemSelection(menuItem, this.category(), orderSimpleProductsDirectly, ignoreSizes, requiredExtrasOnly, () => directAdd?.reset()));
    }

    public toggleDirectAddSizes(): void {
        this.showSizeSelection.set(!this.showSizeSelection());
        this.setSizeSelectionHeight();
    }

    public selectCategory(event: MouseEvent): void {
        this.menuModel.selectCategory(this.category(), false, event);
    }

    private setSizeSelectionHeight(): void {
        if (this.configurationModel.isWaiterActor() && this.sizeSelectionER()) {
            if (this.showSizeSelection()) {
                this.sizeSelectionER()!.nativeElement.style.marginTop = "var(--margin-top)";
                setTimeout(() => {
                    this.sizeSelectionER()!.nativeElement.style.maxHeight = this.sizeSelectionER()!.nativeElement.scrollHeight + "px";
                    this.sizeSelectionER()!.nativeElement.style.minHeight = this.sizeSelectionER()!.nativeElement.style.maxHeight;
                }, 100);
            } else {
                this.sizeSelectionER()!.nativeElement.style.removeProperty("max-height");
                this.sizeSelectionER()!.nativeElement.style.removeProperty("min-height");
                setTimeout(() => this.sizeSelectionER()!.nativeElement.style.removeProperty("margin-top"), 100);
            }
        }
    }
}
